// SpoutingGutterDetailsBar — iter142
//
// Compact echo of the [Gutter Builder] "Gutter Details" panel pinned to
// the Spouting section header in the Components tab. The operator picks:
//   • Gutter Type (Steel / Marley PVC)
//   • Profile     (Quarter Round 150 / 125 Square / Classic / Stormcloud …)
//   • Material    (Zincalume / Galvanised / Maxam / Copper …) — steel only
//   • Bracket Type, Bracket Span (mm), Mitre Extra (mm)
//
// Reads + writes the SAME `/guttering/appraisals/{id}/gutter-builder/config`
// endpoint the Gutter Builder uses, so the two stay in lock-step — pick
// "Marley · Classic" here and the Gutter Builder reflects it on next
// load, and vice versa.
//
// The bar also calls `onContextChange({ material, materialLabel,
// profile, profileLabel, gutterType, bracketType, bracketSpanM,
// mitreExtraMm })` whenever a control flips, so the parent's Spouting
// rate-row cascade can narrow the Type dropdown to rows that mention
// the current material + profile (mirrors how Tile's roof_type / brand
// cascade narrowed its Type list in iter138).

import React, { useEffect, useState, useCallback } from 'react';

export default function SpoutingGutterDetailsBar({
  api,
  appraisalId,
  onContextChange,
}) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  // ── Load gutter-builder config + options on mount ────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: d } = await api.get(
          `/guttering/appraisals/${appraisalId}/gutter-builder/config`,
        );
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [api, appraisalId]);

  // ── Push context up so the Spouting cascade can use it. ─────
  useEffect(() => {
    if (!data || !onContextChange) return;
    const cfg = data.config || {};
    const opt = data.options || {};
    const profList = cfg.gutter_type === 'marley'
      ? (opt.marley_profiles || [])
      : (opt.steel_profiles || []);
    const matList = opt.steel_materials || [];
    const brList  = opt.bracket_types || [];
    // Iter144 — explicit "All" sentinel: when profile_key === '__all__'
    // or material_key === '__all__' the bar tells the parent to skip
    // that field's narrow, so the operator can see the entire Spouting
    // rate list without unpicking the rest of the gutter context.
    const profileAll  = cfg.profile_key === '__all__';
    const materialAll = cfg.material_key === '__all__';
    const profileLabel = profileAll ? '' : ((profList.find(p => p.key === cfg.profile_key) || {}).label || cfg.profile_key || '');
    const materialLabel = cfg.gutter_type === 'marley'
      ? 'Marley'
      : (materialAll ? '' : ((matList.find(m => m.key === cfg.material_key) || {}).label || cfg.material_key || ''));
    const bracketLabel = (brList.find(b => b.key === cfg.bracket_type) || {}).label || cfg.bracket_type || '';
    onContextChange({
      gutterType:    cfg.gutter_type || 'steel',
      material:      materialAll ? '' : (cfg.material_key || ''),
      materialLabel,
      materialAll,
      profile:       profileAll ? '' : (cfg.profile_key || ''),
      profileLabel,
      profileAll,
      profileLabels: profList.map(p => p.label),
      bracketType:   cfg.bracket_type || '',
      bracketLabel,
      bracketSpanM:  cfg.bracket_span_m ?? 0.7,
      mitreExtraMm:  cfg.mitre_extra_mm ?? 300,
      // Iter149 — also surface buffer % + bracket finish so the
      // Bracket Summary card on the Spouting section can apply the
      // SAME formula as the Gutter Builder without forcing the
      // operator to bounce between the two pages.
      bracketBufferPct: cfg.bracket_buffer_pct ?? 10,
      bracketFinish:    cfg.bracket_finish || 'plain',
      // Iter150-D — Editable Spouting consumable rates piped through
      // for the Bracket Summary's rivet/silicone calc + the inline
      // "⚙ rates" editor below the summary header.
      dropperRivets:           cfg.dropper_rivets ?? 4,
      dropperSiliconePct:      cfg.dropper_silicone_pct ?? 10,
      mitrePairRivets:         cfg.mitre_pair_rivets ?? 5,
      mitrePairSiliconePct:    cfg.mitre_pair_silicone_pct ?? 15,
      stopEndRivets:           cfg.stop_end_rivets ?? 4,
      stopEndSiliconePct:      cfg.stop_end_silicone_pct ?? 15,
      // Iter155 — buffer percentages for rivet + silicone totals on
      // Push to BOM. Falls back to 10/10 when the config doesn't have
      // them (legacy appraisals).
      rivetBufferPct:          cfg.rivet_buffer_pct ?? 10,
      siliconeBufferPct:       cfg.silicone_buffer_pct ?? 10,
    });
  }, [data, onContextChange]);

  // ── Apply patch + persist immediately. The Gutter Builder uses
  // an explicit Save button — but on this compact bar we autosave
  // because the operator's intent is "set the cascade context",
  // which only makes sense once committed. ────────────────────
  const update = useCallback(async (patch) => {
    if (!data) return;
    const next = { ...data.config, ...patch };
    setData(d => ({ ...d, config: next }));
    setBusy(true); setErr(null);
    try {
      await api.put(
        `/guttering/appraisals/${appraisalId}/gutter-builder/config`,
        next,
      );
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1200);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [api, appraisalId, data]);

  if (!data) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs text-slate-500">
        Loading gutter details…
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-800">
        Gutter details: {err}
      </div>
    );
  }

  const cfg = data.config || {};
  const opt = data.options || {};
  const profileOptions = cfg.gutter_type === 'marley'
    ? (opt.marley_profiles || [])
    : (opt.steel_profiles || []);
  const materialOptions = opt.steel_materials || [];
  const bracketTypes = opt.bracket_types || [];

  return (
    <div
      data-testid="spouting-gutter-details-bar"
      className="flex flex-wrap items-end gap-3 bg-cyan-50/40 border border-cyan-200 rounded-md px-3 py-2 text-xs"
    >
      <div className="flex items-center gap-1 mr-1 text-cyan-800 font-semibold uppercase tracking-wide">
        <span>Gutter details</span>
        {busy && <span className="text-cyan-500">·  saving…</span>}
        {savedFlash && <span className="text-emerald-600">·  saved</span>}
      </div>

      <Field label="Gutter Type">
        <select
          data-testid="spouting-gutter-type"
          value={cfg.gutter_type || 'steel'}
          onChange={(e) => update({ gutter_type: e.target.value })}
          className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
        >
          <option value="steel">Steel</option>
          <option value="marley">Marley (PVC)</option>
        </select>
      </Field>

      <Field label="Profile">
        <select
          data-testid="spouting-gutter-profile"
          value={cfg.profile_key || ''}
          onChange={(e) => {
            const valid = opt.bracket_types_by_profile?.[e.target.value] || [];
            const patch = { profile_key: e.target.value };
            if (valid.length && !valid.includes(cfg.bracket_type)) patch.bracket_type = valid.includes('concealed') ? 'concealed' : valid[0];
            update(patch);
          }}
          className="px-2 py-1 text-xs border border-slate-300 rounded bg-white min-w-[10rem]"
        >
          <option value="__all__">All profiles</option>
          {profileOptions.map(p => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
      </Field>

      {cfg.gutter_type === 'steel' && (
        <Field label="Material">
          <select
            data-testid="spouting-gutter-material"
            value={cfg.material_key || ''}
            onChange={(e) => update({ material_key: e.target.value })}
            className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
          >
            <option value="__all__">All materials</option>
            {materialOptions.map(m => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
        </Field>
      )}

      <Field label="Bracket Type">
        <select
          data-testid="spouting-bracket-type"
          value={cfg.bracket_type || ''}
          onChange={(e) => update({ bracket_type: e.target.value })}
          className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
        >
          {bracketTypes.filter(b => !(opt.bracket_types_by_profile?.[cfg.profile_key]?.length)
            || opt.bracket_types_by_profile[cfg.profile_key].includes(b.key)).map(b => (
            <option key={b.key} value={b.key}>{b.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Bracket Span (mm)">
        <input
          type="number" step="50" min="100" max="1500"
          data-testid="spouting-bracket-span"
          value={Math.round((cfg.bracket_span_m || 0.7) * 1000)}
          onChange={(e) => {
            const mm = parseInt(e.target.value, 10);
            const m = (Number.isFinite(mm) && mm > 0) ? mm / 1000 : 0.7;
            update({ bracket_span_m: m });
          }}
          className="px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num w-20"
        />
      </Field>

      <Field label="Mitre Extra (mm)">
        <select
          data-testid="spouting-mitre-extra"
          value={cfg.mitre_extra_mm || 300}
          onChange={(e) => update({ mitre_extra_mm: parseInt(e.target.value, 10) })}
          className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
        >
          {[150, 200, 250, 300, 350, 400, 500].map(v => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </Field>

      {/* Iter149 — Buffer % + Bracket Finish surfaced here so the
          Spouting section's Bracket Summary calculator stays in
          lock-step with the Gutter Builder (same backing config). */}
      <Field label="Buffer %">
        <select
          data-testid="spouting-bracket-buffer-pct"
          value={cfg.bracket_buffer_pct ?? 10}
          onChange={(e) => update({ bracket_buffer_pct: parseFloat(e.target.value) })}
          className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
        >
          {[0, 5, 10, 15, 20].map(v => (
            <option key={v} value={v}>{v}%</option>
          ))}
        </select>
      </Field>

      <Field label="Bracket Finish">
        <select
          data-testid="spouting-bracket-finish"
          value={cfg.bracket_finish || 'plain'}
          onChange={(e) => update({ bracket_finish: e.target.value })}
          className="px-2 py-1 text-xs border border-slate-300 rounded bg-white"
        >
          <option value="plain">Plain</option>
          <option value="colour">Painted</option>
        </select>
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}
